import {forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import {HintTypography, StandardSemiBoldTypography} from 'patient-ping-remedy/packages/typography';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import ProgressIndicator, {lighterGreen, StepOptions} from 'patient-ping-remedy/packages/progress-indicator';
import {
  CareJourneyDetailsResponse,
  CareJourneyStep,
  CareJourneyType,
  ModuleType,
  ScheduleAppointmentJourneyResponse,
  TaskStepName,
  UserTaskType
} from '@/api/dto/care-journey';
import {useTaskStepName} from '@/hooks/useTaskStepName';
import TaskEditDetails from '@/components/pages/tasks/task_edit/progress/TaskEditDetails';
import TaskEditStep from '@/components/pages/tasks/task_edit/progress/TaskEditStep';
import WorkflowStep from '@/components/pages/tasks/task_edit/progress/WorkflowStep';
import {
  PRE_REVIEW_STEP_COUNT,
  PRE_REVIEW_STEP_LABELS,
  PreReviewWizardContext,
} from '@/components/pages/tasks/task_edit/progress/pre_review/PreReviewWizardContext';
import {
  clientProfileErrors,
  PreReviewForm,
  reviewReferralErrors,
  toInitialForm,
} from '@/components/pages/tasks/task_edit/progress/pre_review/preReviewHelpers';
import {
  AppointmentLabel,
  ContentSection,
  Progress,
  ProgressSection,
  ProgressSectionContent,
  ProgressSectionInner,
  ProgressText,
  ProgressWrapper,
  StepLabel,
  Wrapper
} from '@/components/pages/tasks/task_edit/progress/TaskEditProgress.styles';
import {FilterInvalidate} from "@common/FilterComponent";
import {css} from "@emotion/css";
import {
  appointmentTypes,
  getAppointmentStatus
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import AppointmentDetails from "@/components/pages/tasks/task_edit/progress/bh_journey/AppointmentDetails";
import {useNavigate} from "react-router-dom";
import {SearchParam} from "@/helpers/search_params";
import {useActiveRoster} from "@/store/roster_store";

export const statusDisplayValues: Record<string, { label: string, icon?: ICONS }> = {
  pending: {
    label: 'Pending',
  },
  in_progress: {
    label: 'In progress',
    icon: ICONS.circle,
  },
  skipped: {
    label: 'Skipped',
    icon: ICONS.circle,
  },
  completed: {
    label: 'Completed',
    icon: ICONS.check,
  },
};

export type TaskEditProgressRef = {
  changeTab(index: number): void;
}

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
  fetching: boolean;
  disabled: boolean;
  invalidate: FilterInvalidate;
}

const appointmentJourneyTypes = [
  CareJourneyType.BH_APPOINTMENT.valueOf(),
  CareJourneyType.BH_SCHEDULE_APPOINTMENT.valueOf()
];

const TaskEditProgress = forwardRef<TaskEditProgressRef, Props>((
  { careJourneyDetails, fetching, disabled, invalidate }, ref) => {
  const { taskStepDisplayValues } = useTaskStepName();
  const [current, setCurrent] = useState(-1);
  const currentRoster = useActiveRoster();
  const navigate = useNavigate();

  const value = careJourneyDetails.steps.filter((step) => step.status === 'completed').length;
  const max = careJourneyDetails.steps.length;

  // The pre-review USER_TASK (CARECO-5758) runs a 3-step wizard. For that journey the host rail
  // surfaces those sub-steps instead of the raw workflow steps, and shares the active sub-step with
  // the content component via context — the component no longer renders its own stepper (CARECO-6078).
  const [preReviewSubStep, setPreReviewSubStep] = useState(0);
  // Dismissed instruction banners, tracked by stable id at the wizard-state level (alongside
  // preReviewSubStep) so a dismissal survives step switches — the step content remounts but this
  // host does not — and resets when the task is re-opened (CARECO-6086).
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set());
  const dismissBanner = useCallback((id: string) => {
    setDismissedBanners(prev => new Set(prev).add(id));
  }, []);
  const preReviewIndex = careJourneyDetails.orchestrationEngineDetails
    ? -1
    : careJourneyDetails.steps.findIndex(
      (step) => step.moduleType === ModuleType.USER_TASK
        && step.userTaskType === UserTaskType.CARE_JOURNEY_PRE_REVIEW
    );
  const isPreReview = preReviewIndex >= 0;

  // Reviewer working set, hosted (like preReviewSubStep/dismissedBanners) so unsaved field edits and
  // picker selections survive the content unmount when the reviewer opens the Details view and
  // returns (CARECO-6283). Seeded from the step prefill; reset when the task changes (below).
  const preReviewReviewData = isPreReview
    ? careJourneyDetails.steps[preReviewIndex]?.reviewData
    : undefined;
  const [preReviewForm, setPreReviewForm] = useState<PreReviewForm>(() => toInitialForm(preReviewReviewData));

  // The task detail page is not remounted when the `t` search param changes, so this host (and its
  // state) persists across journeys. Reset the wizard sub-step and clear dismissed banners when a
  // different journey — or a replacement pre-review task within the same journey (its taskId
  // changes) — loads, so it starts at step 0, re-runs the profile-validation guard, and shows the
  // instruction banners again (CARECO-6167).
  const preReviewTaskId = isPreReview ? careJourneyDetails.steps[preReviewIndex]?.taskId : undefined;
  useEffect(() => {
    setPreReviewSubStep(0);
    setDismissedBanners(new Set());
    setPreReviewForm(toInitialForm(preReviewReviewData));
    // Reset only on a new journey or a replacement task — NOT on a same-task refetch — so
    // in-progress edits are preserved across refetches (CARECO-6167); hence preReviewReviewData is
    // intentionally excluded from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careJourneyDetails.careJourneyInstanceId, preReviewTaskId]);

  // Forward-navigation guard used when the wizard is NOT mounted (e.g. the Details view). Mirrors
  // CareJourneyPreReview.canNavigateToStep against the hosted working set (CARECO-6283) so unsaved
  // edits are respected: always allow returning to the profile (step 0); block advancing while the
  // client profile is invalid; and block jumping to the Create step (>= 2) while the referral is
  // invalid, landing on Review Referral (substep 1) so the blocked jump is visible (CARECO-6167).
  const childGuardRef = useRef<((target: number) => boolean) | null>(null);
  const setStepGuard = useCallback((guard: ((target: number) => boolean) | null) => {
    childGuardRef.current = guard;
  }, []);
  const prefillStepGuard = useCallback((target: number) => {
    if (!isPreReview || target === 0) {
      return true;
    }
    if (Object.keys(clientProfileErrors(preReviewForm)).length > 0) {
      return false;
    }
    if (target >= 2 && Object.keys(reviewReferralErrors(preReviewForm)).length > 0) {
      setPreReviewSubStep(1);
      return false;
    }
    return true;
  }, [isPreReview, preReviewForm]);
  const requestPreReviewStep = useCallback((target: number) => {
    const guard = childGuardRef.current ?? prefillStepGuard;
    if (guard(target)) {
      setPreReviewSubStep(target);
    }
  }, [prefillStepGuard]);
  const railCurrent = isPreReview ? (current === -1 ? -1 : preReviewSubStep) : current;
  // A completed pre-review USER_TASK must always read 3/3 with every sub-step Completed, regardless
  // of which read-only screen is currently visited (CARECO-6287). Without this the rail reflects the
  // visited wizard sub-step (0/3 on reopen, then 1/3, 2/3 as the navigator pages through the
  // read-only screens) instead of the finished review state.
  const preReviewCompleted =
    isPreReview && careJourneyDetails.steps[preReviewIndex]?.status === 'completed';
  const isSubStepComplete = (index: number) => preReviewCompleted || index < preReviewSubStep;
  const progressValue = isPreReview
    ? (preReviewCompleted ? PRE_REVIEW_STEP_COUNT : preReviewSubStep)
    : value;
  const progressMax = isPreReview ? PRE_REVIEW_STEP_COUNT : max;

  const preReviewRailSteps = [
    {
      label: (<StepLabel>Details</StepLabel>) as unknown as string,
      value: -1,
      color: colors.blue4,
      icon: (step: StepOptions) =>
        <Icon iconClass={ICONS.info} color={step.value === railCurrent ? colors.white : step.color} />,
    },
    ...PRE_REVIEW_STEP_LABELS.map((label, index) => ({
      label: (<StepLabel>{label}</StepLabel>) as unknown as string,
      value: index,
      description: (isSubStepComplete(index)
        ? 'Completed'
        : index === preReviewSubStep ? 'In progress' : 'Not started') as unknown as string,
      color: railCurrent === index ? colors.green4 : lighterGreen,
      icon: (stepValue: StepOptions) =>
        <Icon
          iconClass={isSubStepComplete(index) ? ICONS.check : ICONS.circle}
          iconSize={isSubStepComplete(index) ? 'sm' : 'xs'}
          color={stepValue.value === railCurrent ? colors.white : stepValue.color}
        />,
    })),
  ];

  useImperativeHandle(ref, () => {
    return {
      changeTab(tabIndex: number) {
        setCurrent(tabIndex);
      },
    };
  }, []);
  
  const getDescription = (step: CareJourneyStep) => {
    if(step.stepName !== TaskStepName.SCHEDULED_APPOINTMENTS) {
      return <Fragment>{statusDisplayValues[step.status]?.label}</Fragment>;
    }

    return <Fragment>
      <div className={css({marginBottom: sizes.small})}>{statusDisplayValues[step.status]?.label}</div>
      <ProgressIndicator
        direction="vertical"
        current={-1}
        steps={step.appointments?.map((appointment, index) => {
          const appointmentStatusConfig = getAppointmentStatus(appointment);
          const needsReview = appointmentStatusConfig.status === 'Needs review';
          const inProgress = appointmentStatusConfig.status === 'In progress';

          return {
            label: appointmentTypes[appointment.providerType as keyof typeof appointmentTypes] ?? appointment.providerType,
            value: index,
            description: appointmentStatusConfig.progressStatus,
            color: needsReview ? colors.white : lighterGreen,
            icon: () => needsReview ? <Icon
              className={css({width: sizes.small, height: sizes.small,})}
              iconClass={ICONS['exclamation-triangle']}
              iconSize="1x"
              color={colors.yellow5}
              weight="far"
              data-testid="ai-agent-fail"
            /> : <Icon
              iconClass={inProgress ? ICONS.circle : ICONS.check}
              color={lighterGreen}
            />
          };
        }) ?? []}
        handleClick={(_, stepOptions) => {
          const appointment = step.appointments?.at(stepOptions?.value ?? 0);
          if(appointment) {
            const searchParams = `${SearchParam.TASK_ID}=${appointment.parentJourneyId}&` +
                `${SearchParam.ROSTER_ID}=${currentRoster?.id}&` +
                `${SearchParam.APPOINTMENT_ID}=${appointment.careJourneyInstanceId}`;
            navigate(`/tasks/details/appointment?${searchParams}`);
          }
        }}
      />
    </Fragment>;
  };

  const navigateToParentJourney = () => {
    const appointment = careJourneyDetails as ScheduleAppointmentJourneyResponse;
    const search = `?${SearchParam.TASK_ID}=${appointment.parentJourneyId}`
    + `&${SearchParam.ROSTER_ID}=${currentRoster?.id}`;
    navigate(`/tasks/details${search}`);
  };

  return (
    <Wrapper>
      <ProgressSection>
        {appointmentJourneyTypes.includes(careJourneyDetails.journeyType) &&
          <div className={css({display: 'flex', padding: sizes.small, paddingBottom: 0})}>
            <div
              className={css({marginRight:  sizes.xxSmall, cursor: 'pointer'})}
              onClick={navigateToParentJourney}
            >
              <Icon
                iconClass={ICONS["angle-left"]}
                iconSize={"2x"}
                weight={"fal"}
                color={colors.primary1}
              />
            </div>
            <div className={css({display: 'flex', flexDirection: 'column', justifyContent: "center"})}>
              <AppointmentLabel>
                {appointmentTypes[(careJourneyDetails as ScheduleAppointmentJourneyResponse).providerType]
                  ?? (careJourneyDetails as ScheduleAppointmentJourneyResponse).providerType}
              </AppointmentLabel>
              <HintTypography>
                BH Care Management
              </HintTypography>
            </div>
          </div>
        }
        <ProgressSectionContent>
          <ProgressSectionInner>
            <ProgressWrapper>
              <ProgressText>
            Complete {progressValue} out of {progressMax} steps
                <StandardSemiBoldTypography>{Math.round((progressValue * 100) / progressMax)}%</StandardSemiBoldTypography>
              </ProgressText>
              <Progress value={progressValue} max={progressMax} />
            </ProgressWrapper>
            <ProgressIndicator
              steps={isPreReview ? preReviewRailSteps : [
                {
                  label: (
                  <StepLabel>
                    {'Details'}
                    {careJourneyDetails.orchestrationEngineDetails
                      && careJourneyDetails.orchestrationEngineDetails.actor === 'human'
                      && careJourneyDetails.orchestrationEngineDetails.reasonStep !== null && (
                      <ToolTip
                        text={"AI Agent couldn't complete " +
                          (taskStepDisplayValues[careJourneyDetails.orchestrationEngineDetails.reasonStep as TaskStepName]
                            ?? careJourneyDetails.orchestrationEngineDetails.reasonStep) +
                          " step"}
                        type="dark"
                      >
                        <Icon
                          iconClass={ICONS['exclamation-triangle']}
                          iconSize="1x"
                          color={colors.yellow5}
                          weight="far"
                          data-testid="ai-agent-fail"
                        />
                      </ToolTip>
                    )}
                  </StepLabel>
              ) as unknown as string,
                  value: -1,
                  color: colors.blue4,
                  icon: (step) =>
                    <Icon iconClass={ICONS.info} color={step.value === current ? colors.white : step.color} />,
                },
                ...careJourneyDetails.steps.map((step, index) => ({
                  label: (
                <StepLabel>
                  {!careJourneyDetails.orchestrationEngineDetails && step.displayLabel
                    ? step.displayLabel
                    : taskStepDisplayValues[step.stepName as TaskStepName] ?? step.stepName}
                </StepLabel>
              ) as unknown as string,
                  value: index,
                  description: getDescription(step) as unknown as string,
                  color: current === index ? colors.green4 : lighterGreen,
                  icon: (stepValue: StepOptions) => statusDisplayValues[step.status]?.icon
                    ? <Icon
                      iconClass={statusDisplayValues[step.status].icon!}
                      iconSize={statusDisplayValues[step.status].icon === ICONS.circle ? 'xs' : 'sm'}
                      color={stepValue.value === current ? colors.white : stepValue.color}
                    />
                    : <div />,
                }))
              ]}
              current={railCurrent}
              direction="vertical"
              highlightCurrent
              handleClick={(_, step) => {
                const nextValue = step?.value ?? 0;
                if (isPreReview) {
                  if (nextValue === -1) {
                    setCurrent(-1);
                  } else {
                    setCurrent(preReviewIndex);
                    requestPreReviewStep(nextValue);
                  }
                } else {
                  setCurrent(nextValue);
                }
              }}
            />
          </ProgressSectionInner>
        </ProgressSectionContent>
      </ProgressSection>
      <ContentSection>
        {current === -1
          ? careJourneyDetails.journeyType === CareJourneyType.BH_SCHEDULE_APPOINTMENT ?
            <AppointmentDetails
              careJourneyDetails={careJourneyDetails as ScheduleAppointmentJourneyResponse}
              changeStep={setCurrent}
              invalidate={invalidate}
              disabled={disabled}
            />
            :  <TaskEditDetails 
              careJourneyDetails={careJourneyDetails} 
              changeStep={setCurrent}
              invalidate={invalidate}
              disabled={disabled} />
          : 
          careJourneyDetails.orchestrationEngineDetails ?
            <TaskEditStep
              patient={careJourneyDetails.clientProfile}
              readOnly={disabled || careJourneyDetails.userAssignment.assignedToAi}
              careJourneyStep={careJourneyDetails.steps[current]}
              careJourneyDetails={careJourneyDetails}
              fetching={fetching}
              invalidate={invalidate}
            /> :
            isPreReview ? (
              <PreReviewWizardContext.Provider
                value={{
                  stepIndex: preReviewSubStep,
                  setStepIndex: setPreReviewSubStep,
                  setStepGuard,
                  dismissedBanners,
                  dismissBanner,
                  form: preReviewForm,
                  setForm: setPreReviewForm,
                }}
              >
                <WorkflowStep
                  careJourneyStep={careJourneyDetails.steps[current]}
                  careJourneyDetails={careJourneyDetails}
                  fetching={fetching}
                  disabled={disabled}
                />
              </PreReviewWizardContext.Provider>
            ) : (
              <WorkflowStep
                careJourneyStep={careJourneyDetails.steps[current]}
                careJourneyDetails={careJourneyDetails}
                fetching={fetching}
                disabled={disabled}
              />
            )}
      </ContentSection>
    </Wrapper>
  );
});

export default TaskEditProgress;
